import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import CloseIcon from "@mui/icons-material/Close";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { Alert, Box, Drawer, Grid, IconButton, Stack, Typography } from "@mui/material";
import { differenceInDays, format } from "date-fns";

import { useCallback, useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/AppLayout";
import Block from "@/components/Block";
import ErrorPage from "@/components/ErrorPage";
import { useCampground } from "@/contexts/AppConfigContext";
import { useBookingContext } from "@/contexts/BookingContext";
import BookingSearchDetails from "@/features/booking/components/BookingSearchDetails";
import BookingSummary from "@/features/booking/components/BookingSummary";
import { useAvailability, useCurrency } from "@/hooks";
import { useStepNavigation } from "@/hooks/useStepNavigation";
import type { Campsite, CampsiteData } from "@/types/api";
import { analytics } from "@/utils/analytics";
import { getTotalGuests } from "@/utils/booking";
import BookingProgressIndicator from "../booking/components/BookingProgressIndicator";
import AlternativeDatesCalendar from "./components/AlternativeDatesCalendar";
import CampsiteCard from "./components/CampsiteCard";
import SearchLayoutSkeleton from "./components/SearchLayoutSkeleton";

export default function Results() {
  const { state, actions } = useBookingContext();
  const { data: campgroundData } = useCampground();
  const { checkAvailability } = useAvailability();
  const { goToStep } = useStepNavigation();
  const { currencyCode } = useCurrency();
  const [campsites, setCampsites] = useState<CampsiteData[]>([]);
  const [isResultsLoaded, setIsResultsLoaded] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [showAlternativeDatesForAccoId, setShowAlternativeDatesforAccoId] = useState<number | null>(
    null,
  );

  // depends on the selected language
  const availabilityCheckUrl = campgroundData.urls?.availability_check;
  const numGuests = getTotalGuests(
    state.selectedPersons,
    campgroundData.campground.persons,
    "human",
  );

  // reset selected campsite if user reopens search results
  useEffect(() => {
    actions.setSelectedCampsite(null);
  }, [actions]);

  useEffect(() => {
    const loadData = async () => {
      setIsResultsLoaded(false);
      setFetchError(false);
      try {
        const availableCampsites = await checkAvailability({
          url: availabilityCheckUrl,
          dateFrom: format(state.dateFrom!, "yyyy-MM-dd"),
          dateUntil: format(state.dateUntil!, "yyyy-MM-dd"),
          currencyCode: currencyCode,
        });
        const data: CampsiteData[] = availableCampsites.map((campsite) => {
          const basicCampsite = campgroundData.campground?.campsites.find(
            (cp) => cp.id === campsite.id,
          );
          return {
            basicCampsite,
            availabilityCampsite: campsite,
          } as CampsiteData;
        });
        setCampsites(data);
        analytics.capture("search_started", {
          date_from: format(state.dateFrom!, "yyyy-MM-dd"),
          date_until: format(state.dateUntil!, "yyyy-MM-dd"),
          nights: differenceInDays(state.dateUntil!, state.dateFrom!),
          accommodation_type: state.selectedCampsiteType,
          num_guests: numGuests,
          num_results: data.filter(
            (c) =>
              c.basicCampsite.campsite_type === state.selectedCampsiteType &&
              c.availabilityCampsite.amount > 0,
          ).length,
        });
      } catch {
        setFetchError(true);
      } finally {
        setIsResultsLoaded(true);
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.dateFrom,
    state.dateUntil,
    state.selectedCampsiteType,
    availabilityCheckUrl,
    currencyCode,
    numGuests,
    campgroundData.campground?.campsites.find,
    checkAvailability,
  ]);

  const filteredCampsites = useMemo(() => {
    return campsites
      .filter(
        (campsiteData) => campsiteData.basicCampsite.campsite_type === state.selectedCampsiteType,
      )
      .sort((a, b) => {
        const aAvailable = a.availabilityCampsite.amount > 0 ? 0 : 1;
        const bAvailable = b.availabilityCampsite.amount > 0 ? 0 : 1;
        return aAvailable - bAvailable;
      });
  }, [campsites, state.selectedCampsiteType]);

  const handleSelectCampsite = (campsite: Campsite) => {
    actions.setSelectedCampsite(campsite);
    analytics.capture("accommodation_selected", {
      accommodation_name: campsite.name,
      accommodation_type: campsite.campsite_type,
      date_from: state.dateFrom ? format(state.dateFrom, "yyyy-MM-dd") : undefined,
      date_until: state.dateUntil ? format(state.dateUntil, "yyyy-MM-dd") : undefined,
      nights,
    });
    goToStep("extras");
  };

  const handleSearchAlternativeDates = useCallback(
    (dateFrom: Date, dateUntil: Date) => {
      actions.setDateFrom(dateFrom);
      actions.setDateUntil(dateUntil);
      setShowAlternativeDatesforAccoId(null);
    },
    [actions],
  );

  const nights = differenceInDays(state.dateUntil!, state.dateFrom!);

  const totalGuests = getTotalGuests(
    state.selectedPersons,
    campgroundData.campground.persons,
    "human",
  );

  if (!isResultsLoaded) {
    return <SearchLayoutSkeleton />;
  }

  if (fetchError) {
    return (
      <ErrorPage
        message={t`Could not load availability. Please check your connection and try again.`}
      />
    );
  }

  return (
    <AppLayout title={<BookingProgressIndicator />}>
      <Grid container spacing={{ xs: 1, sm: 3 }} p={2}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <BookingSummary
            sx={{ display: { xs: "none", sm: "block" }, position: "sticky", top: 115 }}
          />
          <Block sx={{ display: { xs: "block", sm: "none" }, p: 2 }}>
            <BookingSearchDetails />
          </Block>
        </Grid>
        <Grid size={{ xs: 12, sm: 8 }}>
          <Stack spacing={{ xs: 2, sm: 3 }}>
            {filteredCampsites.length > 0 &&
              filteredCampsites.every((c) => c.availabilityCampsite.amount === 0) &&
              filteredCampsites.some((c) => c.availabilityCampsite.can_show_alternative_dates) && (
                <Alert severity="warning">
                  <Trans>
                    Great choice, but popular: your selected dates are already booked. However, we
                    have some suggestions for you:
                  </Trans>
                </Alert>
              )}
            {filteredCampsites.length === 0 && (
              <Box textAlign="center" py={6} px={3}>
                <Stack spacing={2} alignItems="center">
                  <SearchOffIcon sx={{ fontSize: 64, color: "text.disabled" }} />
                  <Typography variant="h2">
                    <Trans>No availability found</Trans>
                  </Typography>
                  <Typography color="text.secondary">
                    <Trans>Try different dates or accommodation type.</Trans>
                  </Typography>
                </Stack>
              </Box>
            )}
            {filteredCampsites.map((campsiteData) => (
              <CampsiteCard
                key={campsiteData.availabilityCampsite.id}
                campsiteData={campsiteData}
                onSelect={handleSelectCampsite}
                onShowAlternativeDates={(accoId) => setShowAlternativeDatesforAccoId(accoId)}
                onSelectAlternativeDates={handleSearchAlternativeDates}
                nights={nights}
                guests={totalGuests}
                dateFrom={state.dateFrom ?? undefined}
              />
            ))}
          </Stack>
        </Grid>
      </Grid>
      <Drawer
        anchor="bottom"
        open={showAlternativeDatesForAccoId !== null}
        onClose={() => setShowAlternativeDatesforAccoId(null)}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "16px 16px 0 0",
              bgcolor: { xs: "background.paper", sm: "background.default" },
              p: { xs: 0, sm: 2 },
              pt: { xs: 1, sm: 2 },
              height: { xs: "100dvh", sm: "80vh" },
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            },
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            flex: 1,
            overflowY: { xs: "hidden", sm: "auto" },
            overflow: { xs: "hidden" },
            minHeight: 0,
            display: { xs: "flex", sm: "block" },
            flexDirection: { xs: "column" },
          }}
        >
          <IconButton
            onClick={() => setShowAlternativeDatesforAccoId(null)}
            sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}
          >
            <CloseIcon />
          </IconButton>
          {showAlternativeDatesForAccoId !== null && (
            <Box
              sx={{
                px: { xs: 0, sm: 2 },
                pb: 2,
                maxWidth: 700,
                mx: "auto",
                width: "100%",
                flex: { xs: 1, sm: "initial" },
                display: { xs: "flex", sm: "block" },
                flexDirection: { xs: "column" },
                minHeight: { xs: 0 },
              }}
            >
              <AlternativeDatesCalendar
                onSearch={handleSearchAlternativeDates}
                accoId={showAlternativeDatesForAccoId}
              />
            </Box>
          )}
        </Box>
      </Drawer>
    </AppLayout>
  );
}
